<script setup lang="ts">
import { useSlots } from "vue";

const {
	title,
	icon,
	width = "40rem",
	hideActions = false,
	noBodyPadding = false,
	persistent = true,
} = defineProps<{
	title: string;
	icon?: string;
	width?: string;
	hideActions?: boolean;
	/**
	 * Renders the default slot without the padded, single-scroll wrapper. Needed
	 * by dialogs whose body is a multi-pane layout that has to reach the card
	 * edges and scroll each pane on its own.
	 */
	noBodyPadding?: boolean;
	/** Set false to allow Escape and click-outside to close. */
	persistent?: boolean;
}>();
const emits = defineEmits(["submit"]);
const visible = defineModel<boolean>("visible");
const allowSubmit = defineModel<boolean>("allowSubmit", {
	default: true,
});
const showConfirmation = defineModel<boolean>("showConfirmation", {
	default: false,
});
const slots = useSlots();

const submit = () => {
	emits("submit");
	// When a confirmation slot is provided, the dialog stays open so the
	// result (e.g. a newly created token) can be shown to the user.
	if (!slots.confirmation) {
		visible.value = false;
	}
};
</script>

<template>
	<v-dialog
		v-model="visible"
		@update:model-value="(value) => (visible = value)"
		:persistent="persistent"
		:width="width"
	>
		<v-card elevation="9" class="help">
			<v-toolbar flat>
				<v-toolbar-title>
					<i v-if="icon" :class="icon" class="mr-2" />
					{{ title }}
				</v-toolbar-title>

				<v-btn icon="mdi-close" class="ml-auto" @click="visible = false" />
			</v-toolbar>

			<v-divider />
			<template v-if="noBodyPadding">
				<slot v-if="!showConfirmation" />
				<slot v-else name="confirmation" />
			</template>
			<div v-else class="modal-body pa-4 d-flex flex-column overflow-y-auto">
				<slot v-if="!showConfirmation" />
				<slot v-else name="confirmation" />
			</div>
			<template v-if="!hideActions">
				<v-divider />
				<v-card-actions>
					<slot :name="showConfirmation ? 'confirmation-actions' : 'actions'">
						<template v-if="showConfirmation">
							<v-spacer />
							<v-btn variant="flat" color="secondary" @click="visible = false">
								Close
							</v-btn>
						</template>
						<template v-else>
							<v-spacer />
							<v-btn color="red" @click="visible = false"> Cancel</v-btn>
							<v-btn
								variant="flat"
								color="secondary"
								:disabled="!allowSubmit"
								@click="submit"
							>
								Submit
							</v-btn>
						</template>
					</slot>
				</v-card-actions>
			</template>
		</v-card>
	</v-dialog>
</template>

<style scoped>
.modal-body > :deep(*) {
	flex-shrink: 0;
}
</style>
